//VehPosListRepeat.js
import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Box } from '@mui/system';
import useClientSize from '../hook/useClientSize';
import { IconButton, Button, Chip, Popover } from '@mui/material';
import { useInterval } from 'react-use';
import VehFilter from '../common/VehFilter/VehFilter';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import TimelineIcon from '@mui/icons-material/Timeline'; // route mode
import WhereToVoteIcon from '@mui/icons-material/WhereToVote'; // itin
import BedtimeIcon from '@mui/icons-material/Bedtime'; // No report at all.
import CellTowerIcon from '@mui/icons-material/CellTower'; // cell position
import GpsOffIcon from '@mui/icons-material/GpsOff'; // no position report 1 day or more
import LocalParkingIcon from '@mui/icons-material/LocalParking'; // no position rep 0.5 hour or more
import GpsFixedIcon from '@mui/icons-material/GpsFixed'; // speed < 2
import NavigationIcon from '@mui/icons-material/Navigation'; // speed >= 2
import InfoIcon from '@mui/icons-material/Info';
import { AppPalette, AppWord, MapValue, TableColumnType } from '../../model/AppConst';
import ResponseAlert from '../message/ResponseAlert';
import { LIST_VEH_POS_AND_TERM_STAT } from './VehPosGql';
import { AppObject } from '../../model/AppConst';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { currentMenuRepo, vehPosFilterCriteriaRepo } from '../../model/CvoModel';
import FlexyTable from '../common/flexytable/FlexyTable';
import SearchBox from '../common/SearchBox';
import { ContentHeader, HeaderLeft, HeaderRight } from '../common/CvoStyled';
import { GET_VEH_INFO } from '../veh_info/VehInfoGql';
import VehInfoView from '../veh_info/VehInfoView';
import ValueUtil from '../../model/ValueUtil';
import { useSnackbar } from 'notistack';

const VehPosListContainer = styled(Box)({
	display:'flex', flexDirection:'column'
});

const TermStateBox = styled(Box)({
    flexGrow:3, alignItems:'center', justifyContent:AppWord.END,textAlign:'right', // paddingLeft:10, paddingRight: 10
});
const ButtonBox = styled(Box)({textAlign:'right'});

const TermStateTable = styled(Box)({border:AppPalette.BorderCCC, fontSize:'0.9rem', padding:10, borderRadius:10});
const TermStateRow = styled(Box)({display:'flex', padding:5});
const TermStateLabel = styled('div')({flexGrow:1, textAlign:'left', color: '#555', fontSize:'0.85rem', paddingRight:10});
const TermStateNumber = styled('div')({textAlign:'right', width:60, color: AppPalette.PrimaryRGB});

const HelpLine = styled(Box)({
    display: 'flex', alignItems: 'center', padding: 10, fontSize: '0.85rem'
});
const SM = 'small';

const StateColumn = {
    id:'-mv-mark', label:'상태', width:50, show: true,
    option: {
        aligh:'center', type: TableColumnType.GRAPH,
        form: (rec, rowIndx) => {
            if(rec.gpsTime===AppWord.NOT_REAL_GPS_TIME) return <BedtimeIcon fontSize={SM} color='disabled' />;
            if(rec.reportAgeSec > 432000) return <GpsOffIcon fontSize={SM} color='warning' />;
            if(rec.reportAgeSec > 86400) return <GpsOffIcon fontSize={SM} color='inherit' />;
            if(rec.posSys==='C' || rec.posSys===null) return <CellTowerIcon fontSize={SM} color='disabled' />
            if(rec.reportAgeSec > 1800)  return <LocalParkingIcon fontSize={SM} color='inherit' />
            if(rec.speed < 2) return <GpsFixedIcon fontSize={SM} color='success'/>;
            return <NavigationIcon fontSize={SM} color='primary' style={{transform: 'rotate('+rec.direction+'deg)'}}/>;
        }
    }
};

function printDoorState(v) {
    if(v===null || v===undefined) return null;
    if(v) return <span style={{color: '#f50'}}>열림</span>; // <LockOpenIcon size={AppWord.SMALL} color={AppPalette.WarnColor}/>;
    else return '닫힘'; // <LockIcon size={AppWord.SMALL} color={AppPalette.DisabledColor}/>;
}

export const MiniTableColumns = [
    { id: 'vehPlates', label: '차량번호', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    StateColumn,
    { id: 'gpsTime', label: '최종보고', width: 165, show: true, option:{align: 'inherit', type: TableColumnType.DATETIME} }
];

const TableColumns = [
    { id: 'vehPlates', label: '차량번호', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'vehAlias', label: '지도표시명', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'gpsTime', label: '최종보고', width: 165, show: true, option:{align: 'inherit', type: TableColumnType.DATETIME} },
    StateColumn,
    { id: 'location', label: '위치', width: 360, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'pwrTypeValue', label: '전원타입', width: 80, show: true, option:{align: 'center', type: TableColumnType.TEXT} },
    { id: 'posSysValue', label: '위치타입', width: 80, show: true, option:{align: 'center', type: TableColumnType.TEXT} },
    { id: 'speed', label: '속도(Km/h)', width: 90, show: true, option:{align: 'center', type: TableColumnType.FLOAT} },
    { id: 'svcCondValue', label: '운행상태', width: 90, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'distKm', label: '누적거리(Km)', width: 100, show: true, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tempr1', label: '전면온도', width: 70, show: true, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tempr3', label: '중면온도', width: 70, show: true, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tempr2', label: '후면온도', width: 70, show: true, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tempr4', label: '온도4', width: 70, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'door0', label: '운전석방향', width: 80, show: true, option:{align: 'center', print: (r,i,v)=>printDoorState(v)} },
    { id: 'door1', label: '보조석방향', width: 80, show: true, option:{align: 'center', print: (r,i,v)=>printDoorState(v)} },
    { id: 'door2', label: '뒷문', width: 60, show: true, option:{align: 'center', print: (r,i,v)=>printDoorState(v)} },
    { id: 'driverName', label: '기사', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'driverMobile', label: '휴대전화', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'termId', label: '단말기번호', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'vehTypeTon', label: '차종-톤수', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'custName', label: '업체명', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'irActionName', label: '업무상태', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'roadName', label: '도로', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'roadMaxSpeed', label: '허용속도', width: 80, show: true, option:{align: 'center', type: TableColumnType.INTEGER} },
    { id: 'cargoRemark', label: '화물정보', width: 120, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'irDist', label: '목적지거리(Km)', width: 120, show: true, option:{align: 'center', type: TableColumnType.FLOAT} },
    { id: 'irMinsText', label: '남은시간', width: 120, show: true, option:{align: 'center', type: TableColumnType.TEXT} },
    { id: 'batteryVolt', label: '배터리전압', width: 90, show: true, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'poiName', label: '현재지점', width: 120, show: true, option:{align: 'center', type: TableColumnType.TEXT} },
    { id: 'poiArrTime', label: '지점도착', width: 165, show: true, option:{align: 'center', type: TableColumnType.DATETIME} },
    { id: 'tachoTempr1', label: '타코온도1', width: 120, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tachoTempr2', label: '타코온도2', width: 120, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tachoTempr3', label: '타코온도3', width: 120, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tachoTempr4', label: '타코온도4', width: 120, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tachoTempr5', label: '타코온도5', width: 120, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tachoTempr6', label: '타코온도6', width: 120, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tachoHumid1', label: '습도1'.label, width: 120, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tachoHumid2', label: '습도2'.label, width: 120, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tachoHumid3', label: '습도3'.label, width: 120, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tachoHumid4', label: '습도4'.label, width: 120, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tachoHumid5', label: '습도5'.label, width: 120, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tachoHumid6', label: '습도6'.label, width: 120, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tachoDist', label: '타코누적거리(Km)', width: 120, show: false, option:{align: 'right', type: TableColumnType.FLOAT} },
    { id: 'tachoRpm', label: 'RPM', width: 100, show: false, option:{align: 'right', type: TableColumnType.INTEGER} },
];

const FetchInterval = 61; // seconds


export default function VehPosListRepeat({
	boxHeight,
	boxWidth,
    onClickItem,
    isMini, // minimized selector
    onClickShowRoute,
    onClickShowItinerary,
}) {
    const [vehPosList, setVehPosList] = useState([]);
    const [timeGotPosList, setTimeGotPosList] = useState(0); // Date().getTime()
    const [filterBoxState, setFilterBoxState] = useState({open:false});
    const [vehInfo, setVehInfo] = useState(null);
    // const [vehFilterCriteria, setVehFilterCriteria] = useState(null);
    const vehPosFilter = useReactiveVar(vehPosFilterCriteriaRepo); // {criteria:{}, filterData: null}
    const [search, setSearch] = useState('');
    const [responseAlert, setResponseAlert] = useState(null);
    const [itemSelected, setItemSelected] = useState(null);
    const [termStat, setTermStat] = useState(null);
    const [statAnchorEl, setStatAnchorEl] = useState(null);
    const [helpAnchorEl, setHelpAnchorEl] = useState(null);
    const currentMenu = useReactiveVar(currentMenuRepo);
    const { enqueueSnackbar } = useSnackbar();
    // const {loading:loadingList, error:errorOfList, data:dataList} = responseList;
    const clientSize = useClientSize();

    let counter = 0;

    // ##### Call GraphQL to get List #####
    const [getVehPosList, responseList] = useLazyQuery(LIST_VEH_POS_AND_TERM_STAT, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetListRepeat(data, option)},
		onError: (error) => {
            setResponseAlert({open:true, error: error, title: "위치가져오기 실패."})
        }
    });
    const [getVehInfoItem, responseVehItem] = useLazyQuery(GET_VEH_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetItem(data, option)},
        onError: (error) => {setResponseAlert({open:true, error: error, title: "차량정보 조회 오류."})}
    });

    const ticker = () => {
        if(currentMenu.menuId===AppWord.MENU_VEH_POS) {
            const now = new Date().getTime() / 1000;
            const diff = now - timeGotPosList;
            if(diff > FetchInterval) {
                // setTimeGotPosList(now);
                counter = FetchInterval; // setCounter(FetchInterval);
                getVehPosList();
            }
            else {
                if(counter>0) counter--; // setCounter(counter - 1);
            }
        }
    };

    useInterval(ticker, 1000);

    useEffect(()=>{
        if(itemSelected) {
            const item = vehPosList.find((one) => {return one.vehId === itemSelected.vehId});
            if(item) setItemSelected(item);
            else setItemSelected(null);
        }
    }, [vehPosList, itemSelected]);

    const onCompleteGetListRepeat = (data, clientOption) => {
        const now = new Date().getTime() / 1000;
        if(data.vehPosList) {
            setVehPosList(data.vehPosList.slice());
            setTimeGotPosList(now);
        }
        else {
            setTimeGotPosList(now - FetchInterval + 5); // try again 5 sec later
        }

        if(data.termsStatItem) {
            setTermStat(data.termsStatItem);
        }
        // else setTermStat(null);
    };

    const onCompleteGetItem = (data, option) => {
        if(data.vehInfoItem) {
            setVehInfo(data.vehInfoItem);
        }
    };

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onRequestReload = () => {
        const diff = (new Date().getTime() / 1000) - timeGotPosList;
        if(diff > 5) {
            // setTimeGotPosList(new Date().getTime() / 1000);
            getVehPosList();
        }
        else {
            enqueueSnackbar(AppWord.NO_RELOAD_IN_5_SEC,
                {variant:AppPalette.WarnColor, autoHideDuration:1500});
        }
    };

    const onChangeSearch = (text) => {
        setSearch(text);
    };

    const onSelectItem = (item) => {
        setItemSelected(item);
		if(onClickItem) onClickItem(item);
    };

    const onCloseFilterBox = () => {setFilterBoxState({open:false});};

    const onClickSearchFilterButton = (event) => {
        setFilterBoxState({
            open:true, 
            top: clientSize.HeaderHeight 
                + clientSize.dataContainerPadding 
                + clientSize.SmallBoxPadding,
            left: event.clientX - 10});
    };

    //const criteria = {vehTonId: [1, 3]};
    const onCompleteFilterBox = (criteria, newFilterData) => {
        const newState = {...filterBoxState};
        newState.open = false;
        setFilterBoxState(newState);
        vehPosFilterCriteriaRepo({criteria:criteria, filterData: newFilterData}); // 실제 필터링은 repository를 반영한 다른 컴포넌트에서 수행.
    };

    const onDoubleClickRow = (record, columnId) => {
        if(columnId==='location' || columnId==='gpsTime') {
            const width = window.screen.width - 80;
            const height = window.screen.height - 100;
            const url = ValueUtil.getUrlBase() + `?job=${MapValue.Show.veh}&vehId=` + record.vehId + "#view=map";
            window.open(url, '_blank', MapValue.PopupOption + `,width=${width},height=${height}`);
        }
        else {
            //  if(columnId==='vehPlates' || columnId==='vehAlias')...
            getVehInfoItem({variables:{vehId: record.vehId}});
            onClickShowRoute(null); // close route view first
        }
    };

    const onClickClearFilter = () => {
       vehPosFilterCriteriaRepo({criteria:null, filterData: null});
    };

    const loadingAny = responseList.loading || responseVehItem.loading;

    const renderTermStateRow = (label, value) => {
        return (
            <TermStateRow>
                <TermStateLabel>{label}</TermStateLabel>
                <TermStateNumber>{value}</TermStateNumber>
            </TermStateRow>
        );
    };

    const renderTermStat = () => {
        if(termStat) {
            const open = Boolean(statAnchorEl);
            const workRate = Math.round((termStat.usingTerm * 1000 / termStat.allTerms)) / 10;

            return(
                <TermStateBox>
                    <Chip label={`사용 단말기 ${termStat.usingTerm}(총${termStat.allTerms})`}
                        onMouseEnter={(e)=>{setStatAnchorEl(e.currentTarget)}}
                        variant={AppPalette.VariantOutlined}
                    />
                    <Popover
                        open={open}
                        anchorEl={statAnchorEl}
                        onClose={()=>setStatAnchorEl(null)}
                        anchorOrigin={{ vertical: AppWord.TOP, horizontal: AppWord.RIGHT, }}
                        transformOrigin={{ vertical: AppWord.TOP, horizontal: AppWord.RIGHT, }}
                    >
                        <TermStateTable
                            onMouseLeave={()=>setStatAnchorEl(null)}
                        >
                            {renderTermStateRow('가입 단말기', termStat.allTerms)}
                            {renderTermStateRow('사용 단말기 ', termStat.usingTerm)}
                            {renderTermStateRow('정상가동', termStat.normalTerm)}
                            {renderTermStateRow('정지단말기', termStat.normalOff)}
                            {renderTermStateRow('가동률', `${workRate}%`)}
                            {renderTermStateRow('지금 보고 중(15분)', termStat.workingTerm)}
                            {renderTermStateRow('사용하지 않는 단말기', termStat.notUsing)}
                            {renderTermStateRow('일시정지', termStat.stoppedTerm)}
                            {renderTermStateRow('사용대기', termStat.waitTerm)}
                        </TermStateTable>
                    </Popover>
                </TermStateBox>
            );
        }
        return null;
    };

    const renderStateHelp = () => {
        return (
            <Box>
                <Chip label="상태 도움말" color="info" variant="outlined" icon={<InfoIcon />}
                    onClick={(e) => setHelpAnchorEl(e.currentTarget)}
                />
                <Popover
                    open={Boolean(helpAnchorEl)} anchorEl={helpAnchorEl} onClose={() => setHelpAnchorEl(null)}
                    anchorOrigin={{ vertical: AppWord.TOP, horizontal: AppWord.RIGHT, }}
                    transformOrigin={{ vertical: AppWord.TOP, horizontal: AppWord.RIGHT, }}
                >
                    <Box style={{backgroundColor: '#eee', borderRadius: 5, padding: 10, fontWeight: 'bold'}}>
                        상태 아이콘 의미
                    </Box>
                    <HelpLine>
                        <BedtimeIcon fontSize={SM} color='disabled' /> : 위치보고 없음 (보고된 적이 없는 단말기)
                    </HelpLine>
                    <HelpLine>
                        <GpsOffIcon fontSize={SM} color='warning' /> : 위치보고 없음 (5일 이상)
                    </HelpLine>
                    <HelpLine>
                        <GpsOffIcon fontSize={SM} color='inherit' /> : 위치보고 없음 (1일 이상)
                    </HelpLine>
                    <HelpLine>
                        <CellTowerIcon fontSize={SM} color='disabled' /> : 기지국 위치(셀 위치)
                    </HelpLine>
                    <HelpLine>
                        <LocalParkingIcon fontSize={SM} color='inherit' /> : 주차 중(30분 이상 이동 없음)
                    </HelpLine>
                    <HelpLine>
                        <GpsFixedIcon fontSize={SM} color='success' /> : 정차 중(시속 2Km 미만)
                    </HelpLine>
                    <HelpLine>
                        <NavigationIcon fontSize={SM} color='primary' style={{transform: 'rotate(45deg)'}} /> : 이동 중(진행방향)
                    </HelpLine>
                </Popover>
            </Box>
        );
    };

    return(
        <VehPosListContainer width={boxWidth}>
            <ContentHeader>
                <HeaderLeft>
                    <SearchBox onChange={onChangeSearch} value={search} />
                    {
                        isMini ? null :
                        <IconButton size='small' onClick={onClickSearchFilterButton}>
                            <FilterAltIcon />
                        </IconButton>
                    }
                    {
                        vehPosFilter.criteria
                        &&
                        <IconButton size={AppWord.SMALL}
                            color="secondary"
                            onClick={onClickClearFilter}
                        >
                            <FilterAltOffIcon />
                        </IconButton>
                    }
                </HeaderLeft>
                <HeaderRight>
                    {
                        isMini ? null : renderTermStat()
                    }
                    {
                        isMini ? null : renderStateHelp()
                    }
                    {
                        isMini ? null :
                        <ButtonBox>
                            <Button
                                disabled={!Boolean(itemSelected) || loadingAny}
                                onClick={()=>{onClickShowRoute(itemSelected)}}
                                variant='contained' color='primary'>
                                <TimelineIcon fontSize='small' /> 차량경로보기
                            </Button>
                            &nbsp;
                            <Button
                                disabled={!Boolean(itemSelected) || loadingAny}
                                onClick={()=>{onClickShowItinerary(itemSelected)}}
                                variant='contained' color='primary'>
                                <WhereToVoteIcon fontSize='small' /> 동정정보보기
                            </Button>
                        </ButtonBox>
                    }
                </HeaderRight>
            </ContentHeader>
            <FlexyTable
                name="veh_pos_list"
                uniqueKey="vehId"
                columns={TableColumns}
                records={vehPosList}
                search={search}
                criteria={isMini ? undefined : vehPosFilter.criteria}
                tools={[]}
                onClickOneRow={onSelectItem}
				selected={itemSelected ? [itemSelected] : null}
                onRequestReload={onRequestReload}
                onRowDoubleClicked={onDoubleClickRow}
                loading={responseList.loading}
                defaultSorter={{id:'vehPlates', type: TableColumnType.TEXT}}
            />
            <VehFilter open={filterBoxState.open}
                top={filterBoxState.top}
                left={filterBoxState.left}
                useCustAgree={true}
                onComplete={onCompleteFilterBox}
                onCloseBox={onCloseFilterBox}
                filterDataNow={vehPosFilter.filterData}
            />
            <VehInfoView
                open={vehInfo !== null}
                item={vehInfo}
                onClose={() => {setVehInfo(null)}}
            />
            <ResponseAlert open={responseAlert ? responseAlert.open : false}
                alertData={responseAlert}
                onClose={() => {setResponseAlert(null)}}/>
        </VehPosListContainer>
    );
}

